import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import CreateIcon from '@mui/icons-material/Create';
import DescriptionIcon from '@mui/icons-material/Description';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FilterFramesOutlinedIcon from '@mui/icons-material/FilterFramesOutlined';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HighlightIcon from '@mui/icons-material/Highlight';
import HtmlIcon from '@mui/icons-material/Html';
import ImageIcon from '@mui/icons-material/Image';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbCircleIcon from '@mui/icons-material/LightbulbCircle';
import LinkIcon from '@mui/icons-material/Link';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PollIcon from '@mui/icons-material/Poll';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import StyleIcon from '@mui/icons-material/Style';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import UploadIcon from '@mui/icons-material/Upload';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import WindowIcon from '@mui/icons-material/Window';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';
import CampaignIcon from '@mui/icons-material/Campaign';
import { SvgIconProps } from '@mui/material';
import { forwardRef, memo } from 'react';
import { mergeDeep } from 'remeda';
import { invertHexColor } from 'utility/styles';
import { bloxColors, bloxIconColors } from '../../themes/constants';
import MovieEditIcon from 'components/CustomIcons/MovieEditIcon';

interface IconMappingValue {
  Component: React.ComponentType<SvgIconProps>;
  defaultProps: Partial<SvgIconProps>;
}

export type IconTypes =
  | '*'
  | 'ad'
  | 'addicon'
  | 'article'
  | 'audio'
  | 'autoawesome'
  | 'import'
  | 'business'
  | 'collection'
  | 'coupon'
  | 'create'
  | 'file'
  | 'form'
  | 'html'
  | 'iframe'
  | 'image'
  | 'import'
  | 'link'
  | 'mediafolder'
  | 'metadata'
  | 'notification'
  | 'pdf'
  | 'pdfdisplayad'
  | 'poll'
  | 'search'
  | 'table'
  | 'template'
  | 'user'
  | 'video'
  | 'windowicon'
  | 'youtube'
  | 'zip'
  // Related content
  | 'relcontent'
  | 'bio'
  | 'correction'
  | 'fact'
  | 'highlights'
  | 'info'
  | 'question'
  | 'quote'
  | 'refer'
  | 'update'
  | 'twitter'
  | 'notifier'
  | string;

const iconMapping = new Map<IconTypes, IconMappingValue>([
  [
    '*',
    {
      Component: QuestionMarkIcon,
      defaultProps: {},
    },
  ],
  [
    'ad',
    {
      Component: AdUnitsIcon,
      defaultProps: {},
    },
  ],
  [
    'addicon',
    {
      Component: AddCircleIcon,
      defaultProps: {},
    },
  ],
  [
    'article',
    {
      Component: DescriptionIcon,
      defaultProps: { style: { fill: bloxIconColors.article } },
    },
  ],
  [
    'audio',
    {
      Component: VolumeUpIcon,
      defaultProps: { style: { fill: bloxIconColors.audio } },
    },
  ],
  [
    'autoawesome',
    {
      Component: AutoAwesomeOutlinedIcon,
      defaultProps: {},
    },
  ],
  [
    'import',
    {
      Component: UploadIcon,
      defaultProps: { style: { fill: bloxColors.blue } },
    },
  ],
  [
    'business',
    {
      Component: BusinessIcon,
      defaultProps: {},
    },
  ],
  [
    'collection',
    {
      Component: CollectionsOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.collection } },
    },
  ],
  [
    'coupon',
    {
      Component: ReceiptIcon,
      defaultProps: {},
    },
  ],
  [
    'create',
    {
      Component: CreateIcon,
      defaultProps: {},
    },
  ],
  [
    'file',
    {
      Component: AttachFileOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.file } },
    },
  ],
  [
    'form',
    {
      Component: ListAltOutlinedIcon,
      defaultProps: {},
    },
  ],
  [
    'html',
    {
      Component: HtmlIcon,
      defaultProps: {
        sx(theme) {
          const isLightMode = theme.palette.mode === 'light';
          return {
            fill: isLightMode ? bloxIconColors.html : invertHexColor(bloxIconColors.html),
          };
        },
      },
    },
  ],
  [
    'iframe',
    {
      Component: FilterFramesOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.iframe } },
    },
  ],
  [
    'image',
    {
      Component: ImageIcon,
      defaultProps: { style: { fill: bloxIconColors.image } },
    },
  ],
  [
    'import',
    {
      Component: FileUploadOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.import } },
    },
  ],
  [
    'link',
    {
      Component: LinkIcon,
      defaultProps: { style: { fill: bloxIconColors.alink } },
    },
  ],
  [
    'mediafolder',
    {
      Component: PermMediaIcon,
      defaultProps: {},
    },
  ],
  [
    'metadata',
    {
      Component: CodeIcon,
      defaultProps: { style: { fill: bloxIconColors.metadata } },
    },
  ],
  [
    'notification',
    {
      Component: NotificationsIcon,
      defaultProps: {},
    },
  ],
  [
    'pdf',
    {
      Component: PictureAsPdfOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.pdf } },
    },
  ],
  [
    'pdfdisplayad',
    {
      Component: PictureAsPdfOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.pdf } },
    },
  ],
  [
    'poll',
    {
      Component: PollIcon,
      defaultProps: { style: { fill: bloxIconColors.poll } },
    },
  ],
  [
    'search',
    {
      Component: SearchIcon,
      defaultProps: {},
    },
  ],
  [
    'table',
    {
      Component: TableChartOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.table } },
    },
  ],
  [
    'template',
    {
      Component: AssignmentIcon,
      defaultProps: { style: { fill: bloxColors.yellow } }, // temp color/logo, will update later
    },
  ],
  [
    'user',
    {
      Component: AccountCircleRoundedIcon,
      defaultProps: { style: { fill: bloxColors.gray } },
    },
  ],
  [
    'video',
    {
      Component: VideocamOutlinedIcon,
      defaultProps: { style: { fill: bloxIconColors.video } },
    },
  ],
  [
    'windowicon',
    {
      Component: WindowIcon,
      defaultProps: {},
    },
  ],
  [
    'youtube',
    {
      Component: YouTubeIcon,
      defaultProps: { style: { fill: bloxIconColors.youtube } },
    },
  ],
  [
    'zip',
    {
      Component: FolderZipIcon,
      defaultProps: { style: { fill: bloxIconColors.zip } },
    },
  ],
  // Related content
  [
    'relcontent',
    {
      Component: StyleIcon,
      defaultProps: { style: { fill: bloxIconColors.relcontent } },
    },
  ],
  [
    'bio',
    {
      Component: PersonIcon,
      defaultProps: { style: { fill: bloxColors.gray1 } },
    },
  ],
  [
    'correction',
    {
      Component: CheckCircleIcon,
      defaultProps: { style: { fill: bloxColors.green } },
    },
  ],
  [
    'fact',
    {
      Component: LightbulbCircleIcon,
      defaultProps: { style: { fill: bloxColors.yellow } },
    },
  ],
  [
    'highlights',
    {
      Component: HighlightIcon,
      defaultProps: { style: { fill: bloxColors.blue } },
    },
  ],
  [
    'info',
    {
      Component: InfoOutlinedIcon,
      defaultProps: { style: { fill: bloxColors.green } },
    },
  ],
  [
    'question',
    {
      Component: QuestionMarkIcon,
      defaultProps: { style: { fill: bloxColors.red } },
    },
  ],
  [
    'quote',
    {
      Component: FormatQuoteIcon,
      defaultProps: {},
    },
  ],
  [
    'refer',
    {
      Component: DriveFileRenameOutlineIcon,
      defaultProps: { style: { fill: bloxColors.gray } },
    },
  ],
  [
    'update',
    {
      Component: UpdateIcon,
      defaultProps: { style: { fill: bloxColors.blue } },
    },
  ],
  [
    'movieedit',
    {
      Component: MovieEditIcon,
      defaultProps: { style: { fill: bloxColors.blue } },
    },
  ],
  [
    'twitter',
    {
      Component: XIcon,
      defaultProps: { style: { fill: bloxColors.black } },
    },
  ],
  [
    'notifier',
    {
      Component: CampaignIcon,
      defaultProps: { style: { fill: bloxColors.gray } },
    },
  ],
]);

export interface IconHelperProps {
  iconType?: IconTypes;
  svgIconProps?: Partial<SvgIconProps>;
}

/**
 * `Use this IconHelper when you need to dynamically render an icon based on a string.`
 *
 * `Else, import the MUI Icon component directly from MUI for better performance.`
 *
 * It also allows additional SVG Icon properties to be passed through the `svgIconProps` prop.
 *
 * Default properties are defined in the `iconMapping` object.
 *
 * @example
 * // Render a 'video' icon with a custom color. Any default svgIconProps properties defined in `iconMapping` will be overridden by the `svgIconProps` prop.
 * <IconHelper iconType="video" svgIconProps={{ style: { fill: '#FFFFFF' }} } />
 *
 * @returns {JSX.Element} The Material-UI Icon component for the given `type`, with the given `svgIconProps` applied, or fallback to a default icon.
 *
 */
export const IconHelper = memo(
  forwardRef<SVGSVGElement, IconHelperProps>(
    ({ iconType, svgIconProps = {} }: IconHelperProps, ref) => {
      // Retrieve the icon data from the mapping, or use the fallback '*'
      const iconData = iconMapping.get(iconType ?? '*') ?? iconMapping.get('*');

      if (!iconData) {
        return null;
      }

      const { Component: IconComponent, defaultProps } = iconData;

      // Render guard
      if (!IconComponent) {
        return null;
      }

      // Convert props to a simpler type for merging
      const defaultPropsRecord = defaultProps as Record<string, unknown>;
      const svgIconPropsRecord = svgIconProps as Record<string, unknown>;

      // Merge props deeply while resolving sx separately
      const mergedProps = mergeDeep(
        defaultPropsRecord,
        svgIconPropsRecord,
      ) as SvgIconProps;

      return (
        <IconComponent
          ref={ref}
          id="icon-helper-svg"
          data-testid="icon-helper-svg"
          className="icon-helper-svg"
          {...mergedProps}
        />
      );
    },
  ),
);
IconHelper.displayName = 'IconHelper';
